import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Check, ChevronRight, Loader2 } from "lucide-react";

const FREQUENCIES = ["Daily", "Weekly", "Fortnightly", "Monthly", "Bi-Monthly", "Quarterly", "Half-Yearly", "Yearly"];
const LANGUAGES = ["Hindi", "English", "Marathi", "Sanskrit", "Tamil", "Bengali", "Gujarati", "Kannada", "Telugu", "Malayalam", "Punjabi"];
const CATEGORIES = ["News", "Literary", "Religious", "Technical", "Academic", "Children", "Health", "Business"];

const schema = z.object({
  organization_id: z.string().min(1, "Required"),
  publication_house_id: z.string().min(1, "Required"),
  title: z.string().min(2, "Min 2 chars"),
  title_code: z.string().optional().or(z.literal("")),
  registration_number: z.string().optional().or(z.literal("")),
  registration_date: z.string().optional().or(z.literal("")),
  issn_number: z.string().optional().or(z.literal("")),
  frequency: z.enum(FREQUENCIES, { errorMap: () => ({ message: "Required" }) }),
  language: z.string().min(1, "Required"),
  category: z.string().optional().or(z.literal("")),
  printing_press_details: z.string().optional().or(z.literal("")),
  place_of_publication: z.string().optional().or(z.literal("")),
  editor_name: z.string().optional().or(z.literal("")),
  news_agency_tieups: z.string().optional().or(z.literal("")),
});

const STEPS = [
  { key: "general", title: "General Info", fields: ["organization_id", "publication_house_id", "title", "language", "category"] },
  { key: "registration", title: "Registration Details", fields: ["title_code", "registration_number", "registration_date", "issn_number"] },
  { key: "logistics", title: "Frequency & Logistics", fields: ["frequency", "printing_press_details", "place_of_publication", "editor_name", "news_agency_tieups"] },
];

function Required() { return <span className="text-rose-600">*</span>; }

export default function PeriodicalForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pubsLoading, setPubsLoading] = useState(false);
  const [loadingEntity, setLoadingEntity] = useState(isEdit);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, watch, setValue, reset, trigger, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { frequency: "Monthly", language: "Hindi" },
  });

  const orgId = watch("organization_id");
  const pubId = watch("publication_house_id");
  const freq = watch("frequency");
  const lang = watch("language");
  const cat = watch("category");

  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ } })(); }, []);

  useEffect(() => {
    if (!orgId) { setPubs([]); return; }
    setPubsLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } });
        setPubs(data);
      } catch { /* */ }
      finally { setPubsLoading(false); }
    })();
  }, [orgId]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/periodicals/${id}`);
        const pub = await api.get(`/publication-houses/${data.publication_house_id}`);
        // Pre-seed the pubs list with the loaded pub house so the Select can render its label
        // immediately, even before the active-list fetch (triggered by org_id) completes.
        setPubs((prev) => {
          const exists = prev.some((p) => p.id === pub.data.id);
          return exists ? prev : [{ id: pub.data.id, name: pub.data.name }, ...prev];
        });
        reset({
          organization_id: pub.data.organization_id,
          publication_house_id: data.publication_house_id,
          title: data.title || "",
          title_code: data.title_code || "",
          registration_number: data.registration_number || "",
          registration_date: data.registration_date ? data.registration_date.slice(0, 10) : "",
          issn_number: data.issn_number || "",
          frequency: data.frequency,
          language: data.language,
          category: data.category || "",
          printing_press_details: data.printing_press_details || "",
          place_of_publication: data.place_of_publication || "",
          editor_name: data.editor_name || "",
          news_agency_tieups: data.news_agency_tieups || "",
        });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
      finally { setLoadingEntity(false); }
    })();
  }, [id, isEdit, reset]);

  const next = async () => {
    const ok = await trigger(STEPS[step].fields);
    if (ok) setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = { ...values };
      delete payload.organization_id;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (isEdit) {
        await api.put(`/periodicals/${id}`, payload);
        toast.success("Periodical updated");
      } else {
        await api.post("/periodicals", payload);
        toast.success("Periodical created");
      }
      navigate("/periodicals");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  // Wrap RHF's validation+submit so the final "Save" button can call it explicitly.
  const submitFromButton = handleSubmit(onSubmit);

  return (
    <div data-testid="periodical-form-page" className="mx-auto max-w-4xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/periodicals"><ArrowLeft size={14} className="mr-1" />Back</Link>
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
        {isEdit ? "Edit Periodical" : "Create Periodical"}
      </h1>

      {loadingEntity ? (
        <div className="mt-6 flex items-center gap-2 rounded-sm border border-slate-200 bg-white p-6 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin" /> Loading periodical…
        </div>
      ) : (
      <>
      <div className="mt-2 text-sm text-slate-500">
        Step <span className="font-semibold text-slate-900">{step + 1}</span> of {STEPS.length} · <span className="font-medium text-[#002FA7]">{STEPS[step].title}</span>
      </div>
      <div data-testid="periodical-stepper" className="mt-4 flex items-center gap-2">
        {STEPS.map((s, i) => {
          const isDone = i < step;
          const isCurrent = i === step;
          return (
            <div key={s.key} className="flex items-center gap-2">
              <div className={`flex h-9 w-9 items-center justify-center rounded-sm border-2 text-xs font-bold transition-colors ${
                isDone ? "border-[#002FA7] bg-[#002FA7] text-white"
                : isCurrent ? "border-[#002FA7] bg-[#002FA7] text-white ring-4 ring-[#002FA7]/20"
                : "border-slate-200 bg-white text-slate-400"
              }`}>{isDone ? <Check size={16} /> : i + 1}</div>
              <div className={`text-sm transition-colors ${
                isCurrent ? "font-bold text-[#002FA7]"
                : isDone ? "font-medium text-slate-700"
                : "text-slate-400"
              }`}>{s.title}</div>
              {i < STEPS.length - 1 && <div className={`mx-2 h-0.5 w-10 transition-colors ${isDone ? "bg-[#002FA7]" : "bg-slate-200"}`} />}
            </div>
          );
        })}
      </div>

      <div
        data-testid="periodical-form"
        className="mt-6 space-y-6 rounded-sm border border-slate-200 bg-white p-6"
      >
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              Step {step + 1} of {STEPS.length}
            </div>
            <h2 className="mt-0.5 text-lg font-semibold text-slate-900">{STEPS[step].title}</h2>
          </div>
          {step === STEPS.length - 1 ? (
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 border border-emerald-200">
              <Check size={12} /> Final step — review &amp; save
            </span>
          ) : (
            <span className="text-[11px] text-slate-500">
              Click <span className="font-semibold text-[#002FA7]">Next</span> to continue · nothing is saved until the final step
            </span>
          )}
        </div>
        {step === 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Organization" required error={errors.organization_id?.message} full>
              <Select value={orgId || ""} onValueChange={(v) => { setValue("organization_id", v, { shouldValidate: true }); setValue("publication_house_id", ""); }}>
                <SelectTrigger data-testid="per-org-select" className="rounded-sm"><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Publication House" required error={errors.publication_house_id?.message} full>
              <Select value={pubId || ""} onValueChange={(v) => setValue("publication_house_id", v, { shouldValidate: true })} disabled={!orgId || pubsLoading}>
                <SelectTrigger data-testid="per-pub-select" className="rounded-sm">
                  {pubsLoading ? (
                    <span className="inline-flex items-center gap-2 text-slate-400"><Loader2 size={14} className="animate-spin" />Loading publication houses…</span>
                  ) : (
                    <SelectValue placeholder={orgId ? "Select publication house" : "Select organization first"} />
                  )}
                </SelectTrigger>
                <SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Title of Periodical" required error={errors.title?.message} full>
              <Input data-testid="per-title-input" {...register("title")} className="rounded-sm" />
            </Field>
            <Field label="Language" required error={errors.language?.message}>
              <Select value={lang || ""} onValueChange={(v) => setValue("language", v, { shouldValidate: true })}>
                <SelectTrigger data-testid="per-lang-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category">
              <Select value={cat || ""} onValueChange={(v) => setValue("category", v)}>
                <SelectTrigger data-testid="per-cat-select" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Title Code (PRGI)">
              <Input data-testid="per-tcode-input" {...register("title_code")} className="rounded-sm font-mono" />
            </Field>
            <Field label="RNI/PRGI Reg Number">
              <Input data-testid="per-rni-input" {...register("registration_number")} className="rounded-sm font-mono" placeholder="RNI-XXXXX/YYYY/NNNNN" />
            </Field>
            <Field label="Registration Date">
              <Input type="date" data-testid="per-regdate-input" {...register("registration_date")} className="rounded-sm" />
            </Field>
            <Field label="ISSN">
              <Input data-testid="per-issn-input" {...register("issn_number")} className="rounded-sm font-mono" placeholder="0000-0000" />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Frequency" required error={errors.frequency?.message}>
              <Select value={freq || ""} onValueChange={(v) => setValue("frequency", v, { shouldValidate: true })}>
                <SelectTrigger data-testid="per-freq-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Place of Publication">
              <Input data-testid="per-place-input" {...register("place_of_publication")} className="rounded-sm" />
            </Field>
            <Field label="Editor Name">
              <Input data-testid="per-editor-input" {...register("editor_name")} className="rounded-sm" />
            </Field>
            <Field label="News Agency Tie-ups">
              <Input data-testid="per-agency-input" {...register("news_agency_tieups")} className="rounded-sm" />
            </Field>
            <Field label="Printing Press Details" full>
              <Textarea data-testid="per-press-input" {...register("printing_press_details")} rows={2} className="rounded-sm" />
            </Field>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 pt-4">
          <Button type="button" variant="outline" onClick={back} disabled={step === 0} className="rounded-sm" data-testid="per-step-back">Back</Button>
          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={next} data-testid="per-step-next" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
              Next <ChevronRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button type="button" onClick={submitFromButton} disabled={submitting} data-testid="per-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
              <Save size={14} className="mr-1.5" />
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create Periodical"}
            </Button>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

function Field({ label, required, error, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1 block text-sm font-medium text-slate-700">
        {label} {required && <Required />}
      </Label>
      {children}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
